import {
  featureFlagsApiRef,
  identityApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import { atom, useAtom } from 'jotai';
import { isEqual, mergeWith } from 'lodash';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useEffectOnce } from 'react-use';
import { pdpFilterRef } from '@internal/backstage-plugin-pdp-filter-frontend';
import {
  readFilters,
  isCustomTeamsFilter,
  saveFilters,
  defaultFilterConfig,
  isHrReportsByLeaderTeamFilter,
  getAllChecks,
  InsightsSelectedFilters,
  DEFAULT_INSIGHTS_SELECTED_FILTERS,
} from '../utils/filters';
import { CategoryNode } from '../utils/category';
import { resolveVisibleChecks } from '../feature-flags/visible-checks';
import { yardstickApiRef } from '../api';
import {
  NavigationType,
  useNavigationType,
  useSearchParams,
} from 'react-router-dom';
import { useUserInfo } from './use-user-info';
import { getEnv } from '../utils/env';

export const loadingAtom = atom(true);
export const summaryLoadingAtom = atom(true);

export type DeferState =
  | 'idle'
  | 'loading'
  | 'success'
  | 'error'
  | 'not-authorized';
/** Persists defer action state across modal open/close, keyed by issueId. */
export const deferStateAtom = atom<Record<string, DeferState>>({});
export const summaryErrorAtom = atom(false);
export const errorAtom = atom(false);
export const siteErrorAtom = atom(false);
const departmentErrorAtom = atom(false);
const ownerErrorAtom = atom(false);
const businessUnitsErrorAtom = atom(false);
const measurementChecksLoadingAtom = atom(true);
export const measurementChecksAtom = atom<string[]>([]);
export const adminShowIssuesAtom = atom(false);
/** Increment to trigger a refetch of table data (e.g. after remediation) */
export const remediationVersionAtom = atom(0);

/**
 * Optimistic overrides for remediation lifecycle status, keyed by issueId.
 * Used to immediately reflect state changes in both the badge and context menu
 * before the next server refetch.
 */
export const optimisticRemediationStatusAtom = atom<
  Record<string, 'in_progress'>
>({});

/**
 * Reads the ys-zero-day feature flag from localStorage without requiring
 * React hooks. Backstage stores active flags in localStorage key "featureFlags"
 * as { [flagName]: 1 } (FeatureFlagState.Active === 1).
 */
const isZeroDayFlagActive = (): boolean => {
  try {
    const raw = window.localStorage.getItem('featureFlags');
    if (!raw) return false;
    const flags = JSON.parse(raw);
    return flags['ys-zero-day'] === 1;
  } catch {
    return false;
  }
};

// Synchronously strip zero-day from the URL when the flag is off, before React
// renders, so no history entry is created and no API call ever sees the wrong value.
(() => {
  const flagOn = isZeroDayFlagActive();
  const searchParams = new URLSearchParams(window.location.search);
  const severity = searchParams.get('severity');

  if (!flagOn && severity?.includes('zero-day')) {
    // Flag OFF → remove zero-day from URL
    const cleaned = severity.split(',').filter(s => s.trim() !== 'zero-day');
    if (cleaned.length > 0) {
      searchParams.set('severity', cleaned.join(','));
    } else {
      searchParams.delete('severity');
    }
    const query = searchParams.toString();
    window.history.replaceState(
      {},
      '',
      query ? `${window.location.pathname}?${query}` : window.location.pathname,
    );
  }
})();

// Initial default filters (will be updated once measurement data is loaded).
// Apply the same zero-day logic so the atom state matches the URL from the start.
const initDefaultFilters = (): InsightsSelectedFilters => {
  const parsed = readFilters(
    new URLSearchParams(window.location.search),
  ) as InsightsSelectedFilters;
  const flagOn = isZeroDayFlagActive();

  if (!flagOn && parsed.severity.includes('zero-day')) {
    return {
      ...parsed,
      severity: parsed.severity.filter((s: string) => s !== 'zero-day'),
    };
  }
  return parsed;
};

const defaultFilters = initDefaultFilters();

const env = getEnv();

const filtersAtom = atom(defaultFilterConfig(env));

// Atoms for applied and draft state
export const appliedAtom = atom<Record<string, any>>(defaultFilters);
const draftStoreAtom = atom<Record<string, any>>(defaultFilters);

const draftAtom = atom(
  get => get(draftStoreAtom),
  (get, set, update) => {
    const draft = mergeWith(
      {},
      get(draftStoreAtom),
      update,
      // eslint-disable-next-line consistent-return
      (objValue, srcValue) => {
        if (Array.isArray(objValue)) {
          return srcValue;
        }
      },
    );
    set(draftStoreAtom, draft);
  },
);

// Atom for determining if draft is different from applied
const isDraftDifferentAtom = atom(
  get => !isEqual(get(appliedAtom), get(draftAtom)),
);

const applyDraftAtom = atom(null, (get, set) => {
  const draftValue = get(draftAtom);

  set(appliedAtom, draftValue);

  // Force re-render by dispatching an event
  setTimeout(() => {
    window.dispatchEvent(new Event('filtersApplied'));
  }, 0);
});

const resetDraftAtom = atom(null, (get, set) => {
  set(draftAtom, get(appliedAtom));
});

// Atom to reset filters to default values (including resetting team view)
const resetToDefaultAtom = atom(null, (_get, set) => {
  // Reset everything to default values including team view
  const resetValues = { ...DEFAULT_INSIGHTS_SELECTED_FILTERS };

  set(draftAtom, resetValues);
  set(appliedAtom, resetValues);

  // Force re-render by dispatching an event
  setTimeout(() => {
    window.dispatchEvent(new Event('filtersApplied'));
  }, 0);
});

// Helper function to compare arrays as sets (order-independent)
const arraysEqualAsSet = (arr1: string[], arr2: string[]): boolean => {
  if (arr1.length !== arr2.length) return false;
  const sorted1 = [...arr1].sort();
  const sorted2 = [...arr2].sort();
  return isEqual(sorted1, sorted2);
};

// Atom to check if current filters are at default state
const isAtDefaultAtom = atom(get => {
  const current = get(appliedAtom);
  const defaults = DEFAULT_INSIGHTS_SELECTED_FILTERS;

  // Check priority (order-independent)
  const priorityAtDefault = arraysEqualAsSet(
    current.priority,
    defaults.priority,
  );

  // Check severity (order-independent)
  const severityAtDefault = arraysEqualAsSet(
    current.severity,
    defaults.severity,
  );

  // Check measurementChecksExclude (empty array = default)
  const measurementChecksAtDefault =
    current.measurementChecksExclude.length === 0;

  // Check isCustom
  const isCustomAtDefault = current.isCustom === defaults.isCustom;

  // For custom grouping, also check if child filters are cleared
  if (
    isCustomTeamsFilter(current.teams) &&
    current.teams.customTeamFilterType === 'custom'
  ) {
    const childFiltersCleared =
      (!current.teams.departments || current.teams.departments.length === 0) &&
      (!current.teams.owners || current.teams.owners.length === 0) &&
      (!current.teams.businessUnits ||
        current.teams.businessUnits.length === 0);

    return (
      priorityAtDefault &&
      severityAtDefault &&
      measurementChecksAtDefault &&
      isCustomAtDefault &&
      childFiltersCleared
    );
  }

  // For non-custom team views, check all non-team filters
  return (
    priorityAtDefault &&
    severityAtDefault &&
    measurementChecksAtDefault &&
    isCustomAtDefault
  );
});

const isCustomAtom = atom(
  get => get(draftAtom).isCustom,
  (get, set, update) => {
    const draft = get(draftAtom);
    set(draftAtom, { ...draft, isCustom: update });
  },
);

export const useFilters = () => {
  const [applied] = useAtom(appliedAtom);
  const [draft, setDraft] = useAtom(draftAtom);
  const [hasChanges] = useAtom(isDraftDifferentAtom);
  const [, apply] = useAtom(applyDraftAtom);
  const [, reset] = useAtom(resetDraftAtom);
  const [, resetToDefault] = useAtom(resetToDefaultAtom);
  const [isAtDefault] = useAtom(isAtDefaultAtom);
  const [error, setError] = useAtom(errorAtom);
  const [departmentError, setDepartmentError] = useAtom(departmentErrorAtom);
  const [ownerError, setOwnerError] = useAtom(ownerErrorAtom);
  const [businessUnitsError, setBusinessUnitsError] = useAtom(
    businessUnitsErrorAtom,
  );

  const navigationType = useNavigationType();

  const [search] = useSearchParams();
  const [filters, setFilters] = useAtom(filtersAtom);

  // useEffect to only run when the user navigates history and the search params change
  useEffect(() => {
    if (navigationType === NavigationType.Pop) {
      // Use measurement data for parent-child relationship handling
      setDraft(readFilters(search, filters.measurementChecks.values));
      apply();
    }
  }, [
    search,
    setDraft,
    apply,
    navigationType,
    filters.measurementChecks.values,
  ]);

  const filterApi = useApi(pdpFilterRef);
  const yardstickApi = useApi(yardstickApiRef);
  const [measurementChecks, setMeasurementChecks] = useState<string[]>([]);
  const [isCustom, setIsCustom] = useAtom(isCustomAtom);
  const [measurementChecksLoading, setMeasurementChecksLoading] = useAtom(
    measurementChecksLoadingAtom,
  );
  const { isHrLeader, error: hrLeaderError, userId } = useUserInfo();

  useEffect(() => {
    const getHrLeader = async () => {
      try {
        if (isHrLeader) {
          setFilters(prev => {
            const newFilters = { ...prev };

            if (
              Array.isArray(newFilters.teams) &&
              !newFilters.teams.some(
                team => team.title === 'Teams of my HR reports',
              )
            ) {
              newFilters.teams = [
                ...newFilters.teams,
                { title: 'Teams of my HR reports', value: 'hr-reports' },
              ];
            }

            return newFilters;
          });
        }
      } catch (e) {
        setFilters(prev => {
          const newFilters = { ...prev };

          if (
            Array.isArray(newFilters.teams) &&
            !newFilters.teams.some(
              team => team.title === 'Teams of my HR reports failed to load',
            )
          ) {
            newFilters.teams = [
              ...newFilters.teams,
              {
                title: 'Teams of my HR reports failed to load',
                value: 'error',
              },
            ];
          }

          return newFilters;
        });
      }
    };

    getHrLeader();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isHrLeader]);

  useEffect(() => {
    const setNonUserOwnerFilter = async () => {
      if (search.has('owner') && search.get('owner') !== userId) {
        const ownerLastName = search.get('owner')?.split('.')[0];
        const ownerFirstName = search.get('owner')?.split('.')[1].split('_')[0];

        const ownerFullName = `${
          ownerFirstName
            ? ownerFirstName.charAt(0).toUpperCase() + ownerFirstName.slice(1)
            : ''
        } ${
          ownerLastName
            ? ownerLastName.charAt(0).toUpperCase() + ownerLastName.slice(1)
            : ''
        }`;

        if (
          search.has('hr-reports') &&
          search.get('hr-reports') === 'true' &&
          filters.teams.filter(team => team.value === 'owner-hr-reports')
            .length === 0
        ) {
          setFilters(prev => {
            const newFilters = { ...prev };

            if (Array.isArray(newFilters.teams)) {
              newFilters.teams = [
                ...newFilters.teams,
                {
                  title: `Teams of ${ownerFullName}'s HR reports`,
                  value: 'owner-hr-reports',
                },
              ];
            }

            return newFilters;
          });
        } else if (
          filters.teams.filter(team => team.value === 'owner-member').length ===
            0 &&
          filters.teams.filter(team => team.value === 'owner-hr-reports')
            .length === 0
        ) {
          setFilters(prev => {
            const newFilters = { ...prev };

            if (Array.isArray(newFilters.teams)) {
              newFilters.teams = [
                ...newFilters.teams,
                {
                  title: `Teams ${ownerFullName} is a member of`,
                  value: 'owner-member',
                },
              ];
            }

            return newFilters;
          });
        }
      } else {
        setFilters(prev => {
          const newFilters = { ...prev };

          if (Array.isArray(newFilters.teams)) {
            newFilters.teams = newFilters.teams.filter(
              team => team.value !== 'owner-member',
            );
          }

          return newFilters;
        });
      }

      if (!applied.teams.owner) {
        setFilters(prev => {
          const newFilters = { ...prev };

          if (Array.isArray(newFilters.teams)) {
            newFilters.teams = newFilters.teams.filter(
              team =>
                team.value !== 'owner-member' &&
                team.value !== 'owner-hr-reports',
            );
          }

          return newFilters;
        });
      }
    };

    setNonUserOwnerFilter();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applied]);

  const getFilterValues = async () => {
    filterApi
      .getFilterValues('owners')
      .then(data => {
        setFilters(prev => {
          const newFilters = { ...prev };

          newFilters.owners.values = data?.values.map(value => ({
            title: value.split('group:')[1],
            value,
          }));

          return newFilters;
        });
      })
      .catch(() => {
        setOwnerError(true);
        setFilters(prev => {
          const newFilters = { ...prev };
          // If no existing values, provide empty array to maintain filter structure
          if (
            !newFilters.owners.values ||
            newFilters.owners.values.length === 0
          ) {
            newFilters.owners.values = [];
          }
          return newFilters;
        });
      });

    filterApi
      .getFilterValues('departments')
      .then(data => {
        setFilters(prev => {
          const newFilters = { ...prev };

          newFilters.departments.values = data?.values.map(value => ({
            title: value.split('group:')[1],
            value,
          }));

          return newFilters;
        });
      })
      .catch(() => {
        setDepartmentError(true);
        setFilters(prev => {
          const newFilters = { ...prev };
          // If no existing values, provide empty array to maintain filter structure
          if (
            !newFilters.departments.values ||
            newFilters.departments.values.length === 0
          ) {
            newFilters.departments.values = [];
          }
          return newFilters;
        });
      });

    filterApi
      .getFilterValues('businessUnits')
      .then(data => {
        setFilters(prev => {
          const newFilters = { ...prev };

          newFilters.businessUnits.values = data?.values.map(value => ({
            title: value,
            value,
          }));

          return newFilters;
        });
      })
      .catch(() => {
        setBusinessUnitsError(true);
        setFilters(prev => {
          const newFilters = { ...prev };
          // If no existing values, provide common business units as fallback
          if (
            !newFilters.businessUnits.values ||
            newFilters.businessUnits.values.length === 0
          ) {
            newFilters.businessUnits.values = [
              {
                title: 'principal-international',
                value: 'principal-international',
              },
              {
                title: 'benefits-and-protection',
                value: 'benefits-and-protection',
              },
              {
                title: 'retirement-and-income-solutions',
                value: 'retirement-and-income-solutions',
              },
              {
                title: 'principal-asset-management',
                value: 'principal-asset-management',
              },
              {
                title: 'service-center-business-units',
                value: 'service-center-business-units',
              },
            ];
          }
          return newFilters;
        });
      });

    yardstickApi
      .getMeasurementRegistryResults()
      .then(data => {
        setFilters(prev => {
          const newFilters = { ...prev };

          newFilters.measurementChecks.values = data as CategoryNode[];

          // After loading measurements, update filters if needed with proper parent-child relationships
          const searchParams = new URLSearchParams(window.location.search);
          if (
            searchParams.has('measurementChecksExclude') ||
            searchParams.has('measurement-checks-exclude')
          ) {
            // Re-read filters with the measurement data to establish parent-child relationships
            const updatedFilters = readFilters(
              searchParams,
              data as CategoryNode[],
            );
            setDraft(updatedFilters);
            // Update the applied atom directly too
            setTimeout(() => {
              apply();
              // Force a re-render to properly update the UI
              window.dispatchEvent(new Event('filtersApplied'));
            }, 0);
          }

          setMeasurementChecksLoading(false);
          return newFilters;
        });
      })
      .catch(() => {
        setMeasurementChecksLoading(false);
        setError(true);
      });
  };

  useEffectOnce(() => {
    getFilterValues();
  });

  useEffect(() => {
    // Strip zero-day from the filters written to the URL when the feature flag is off.
    // This is the authoritative guard: it catches every code path that can update
    // `applied` (resetToDefault, Pop navigation, direct setApplied calls, etc.).
    const filtersToSave: InsightsSelectedFilters = isZeroDayFlagActive()
      ? (applied as InsightsSelectedFilters)
      : {
          ...(applied as InsightsSelectedFilters),
          severity: (
            (applied as InsightsSelectedFilters).severity ?? []
          ).filter((s: string) => s !== 'zero-day'),
        };
    saveFilters(
      filtersToSave,
      userId ?? '',
      isHrLeader,
      filters.measurementChecks.values,
    );
  }, [applied, userId, isHrLeader, filters.measurementChecks.values]);

  useEffect(() => {
    const allChecks = getAllChecks(filters.measurementChecks.values, applied);
    setMeasurementChecks(allChecks);
  }, [applied, filters.measurementChecks]);

  const selectedTeamsTitle = useMemo(() => {
    return (
      filters.teams.find(team => {
        if (isCustomTeamsFilter(draft.teams)) {
          if (draft.teams.customTeamFilterType === 'copy-url') {
            if (draft.teams.owner) {
              if (draft.teams.owner === userId) {
                if (draft.teams.hrReports && isHrLeader) {
                  return team.value === 'hr-reports';
                }
                return team.value === 'member';
              }

              if (draft.teams.hrReports) {
                return team.value === 'owner-hr-reports';
              }

              return team.value === 'owner-member';
            }
          }

          return team.value === 'custom';
        }

        if (draft.teams === 'hr-reports' && isHrLeader === false) {
          return team.value === 'member';
        }
        return team.value === draft.teams;
      })?.title ?? ''
    );
  }, [filters.teams, draft.teams, userId, isHrLeader]);

  const getters = {
    selectedTeamsTitle,
  };

  return {
    filters,
    applied,
    draft,
    update: setDraft,
    hasChanges,
    reset,
    resetToDefault,
    isAtDefault,
    apply,
    getters,
    measurementChecks,
    isCustom,
    setIsCustom,
    error,
    measurementChecksLoading,
    departmentError,
    ownerError,
    businessUnitsError,
    hrLeaderError,
    registryData: filters.measurementChecks.values, // Export registry data
  };
};

export const useFilteredEntityList = () => {
  const [applied] = useAtom(appliedAtom);
  const [entityList, setEntityList] = useState<string[]>();
  const [maskedEntityRefs, setMaskedEntityRefs] = useState<string[]>([]);
  const [failedChunks, setFailedChunks] = useState<number>(0);
  const [showAllIssues, setShowAllIssues] = useState(false);
  const { isHrLeader, error: hrLeaderError } = useUserInfo();

  const filterApi = useApi(pdpFilterRef);
  const identityApi = useApi(identityApiRef);

  const [, setLoading] = useAtom(loadingAtom);
  const [, setError] = useAtom(siteErrorAtom);
  const prevTeams = useRef<InsightsSelectedFilters['teams'] | undefined>();
  const prevIsHrLeader = useRef<boolean | null | undefined>();
  const entityRequestCounter = useRef(0);

  // Phase 4: Session-level cache for entity ref lists
  const entityRefCacheRef = useRef<{
    key: string;
    entityRefs: string[];
    maskedEntityRefs: string[];
    expiresAt: number;
  } | null>(null);

  useEffect(() => {
    const getEntities = async (requestId: number) => {
      if (
        isCustomTeamsFilter(applied.teams) &&
        applied.teams.customTeamFilterType === 'custom' &&
        applied.teams.owners.length === 0 &&
        applied.teams.departments.length === 0 &&
        applied.teams.businessUnits.length === 0
      ) {
        setEntityList([]);
        setMaskedEntityRefs([]);
        setShowAllIssues(false);
        setLoading(false);
        return;
      }

      const identity = await identityApi.getBackstageIdentity();

      const request: Record<string, string[]> = {};
      const isVolumeTesting = applied.teams === 'volume-testing';

      if (applied.teams === 'member' || isVolumeTesting) {
        request.owners = identity.ownershipEntityRefs;
      } else if (applied.teams === 'hr-reports') {
        if (isHrLeader) {
          request.myhrreports = [identity.userEntityRef.split('/')[1]];
        } else {
          request.owners = identity.ownershipEntityRefs;
        }
      } else if (isHrReportsByLeaderTeamFilter(applied.teams)) {
        request.myhrreports = [applied.teams.leader];
      } else if (
        isCustomTeamsFilter(applied.teams) &&
        applied.teams.customTeamFilterType === 'custom'
      ) {
        request.owners = applied.teams.owners;
        request.departments = applied.teams.departments.map(
          department => department.split('/')[1],
        );
        request.businessUnits = applied.teams.businessUnits;
      } else if (
        isCustomTeamsFilter(applied.teams) &&
        applied.teams.customTeamFilterType === 'copy-url'
      ) {
        if (applied.teams.owner === identity.userEntityRef.split('/')[1]) {
          request.owners = identity.ownershipEntityRefs;
        } else if (applied.teams.hrReports) {
          request.myhrreports = [applied.teams.owner];
        } else {
          request.owner = [applied.teams.owner];
        }
      }

      // Phase 4: Check session cache before making a network request
      const cacheKey = JSON.stringify(request);
      const cached = entityRefCacheRef.current;
      if (cached && cached.key === cacheKey && cached.expiresAt > Date.now()) {
        // Discard stale response if a newer request has been made
        if (requestId !== entityRequestCounter.current) {
          return;
        }
        setEntityList(cached.entityRefs);
        setMaskedEntityRefs(cached.maskedEntityRefs);
        setFailedChunks(0);

        const pdpUser = identity?.ownershipEntityRefs.includes(
          'group:default/is_exe_pdp',
        );
        const isrPdpViewUser = identity?.ownershipEntityRefs.includes(
          'group:default/isr_pdp_view',
        );
        setShowAllIssues(
          (pdpUser || isrPdpViewUser) && cached.maskedEntityRefs.length > 0,
        );
        return;
      }

      // Phase 1: Use the lightweight entity-refs endpoint (not full entities)
      const response = await filterApi.getEntityRefs(
        request,
        true,
        identity.ownershipEntityRefs,
        isVolumeTesting,
      );

      // Discard stale response if a newer request has been made
      if (requestId !== entityRequestCounter.current) {
        return;
      }

      const entityRefs = response?.entityRefs ?? [];
      const masked = response?.maskedEntityRefs ?? [];

      // Phase 4: Store in session cache (5 minute TTL)
      entityRefCacheRef.current = {
        key: cacheKey,
        entityRefs,
        maskedEntityRefs: masked,
        expiresAt: Date.now() + 5 * 60 * 1000,
      };

      setEntityList(entityRefs);
      setMaskedEntityRefs(masked);
      setFailedChunks(response?._meta?.failedChunks ?? 0);

      const pdpUser = identity?.ownershipEntityRefs.includes(
        'group:default/is_exe_pdp',
      );
      const isrPdpViewUser = identity?.ownershipEntityRefs.includes(
        'group:default/isr_pdp_view',
      );
      setShowAllIssues((pdpUser || isrPdpViewUser) && masked.length > 0);
    };

    const teamsSame = isEqual(applied.teams, prevTeams.current);
    const hrLeaderChanged =
      applied.teams === 'hr-reports' && isHrLeader !== prevIsHrLeader.current;

    const hrLeaderStillLoading =
      applied.teams === 'hr-reports' && isHrLeader === null && !hrLeaderError;

    if (hrLeaderStillLoading || (teamsSame && !hrLeaderChanged)) {
      return;
    }

    // Store the current values so we can compare later.
    prevTeams.current = applied.teams;
    prevIsHrLeader.current = isHrLeader;

    entityRequestCounter.current += 1;
    const currentRequestId = entityRequestCounter.current;

    setLoading(true);

    getEntities(currentRequestId).catch(() => {
      setError(true);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applied, isHrLeader]);

  return {
    entities: entityList,
    maskedEntityRefs,
    showAllIssues,
    failedChunks,
  };
};

export const useFilteredFocusList = () => {
  const [applied] = useAtom(appliedAtom);
  const [focusList, setFocusList] = useState<string[]>(
    applied.priority ?? DEFAULT_INSIGHTS_SELECTED_FILTERS.priority,
  );

  useEffect(() => {
    setFocusList(applied.priority);
  }, [applied]);

  return {
    focus: focusList,
  };
};

export const useFilteredSeverityList = () => {
  const [applied] = useAtom(appliedAtom);
  const featureFlagsApi = useApi(featureFlagsApiRef);
  const isZeroDayEnabled = featureFlagsApi.isActive('ys-zero-day');

  const [severityList, setSeverityList] = useState<string[]>(
    (applied.severity ?? DEFAULT_INSIGHTS_SELECTED_FILTERS.severity).filter(
      (s: string) => isZeroDayEnabled || s !== 'zero-day',
    ),
  );

  useEffect(() => {
    setSeverityList(
      applied.severity.filter(
        (s: string) => isZeroDayEnabled || s !== 'zero-day',
      ),
    );
  }, [applied, isZeroDayEnabled]);

  return {
    severity: severityList,
  };
};

export const useFilteredMeasurementChecksList = () => {
  const [applied] = useAtom(appliedAtom);
  const [filters] = useAtom(filtersAtom);
  const [measurementChecks, setMeasurementChecks] = useState<string[]>();
  const [measurementChecksLoading] = useAtom(measurementChecksLoadingAtom);
  const featureFlagsApi = useApi(featureFlagsApiRef);

  useEffect(() => {
    const allChecks = getAllChecks(filters.measurementChecks.values, applied);
    // Layer the per-user feature-flag subtraction onto the exclude-derived
    // check list so gated measurements the user has not enabled are hidden.
    const visibleChecks = resolveVisibleChecks(
      allChecks,
      filters.measurementChecks.values,
      flagKey => featureFlagsApi.isActive(flagKey),
    );
    setMeasurementChecks(visibleChecks);
  }, [applied, filters.measurementChecks.values, featureFlagsApi]);

  return {
    measurementChecks,
    measurementChecksLoading,
    registryData: filters.measurementChecks.values, // Export registry data
  };
};
